import { Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { Reveal } from '../../components/Reveal';
import { Star } from '../../components/icons/WhatsAppIcon';
import { TopicCard } from '../../components/insights/TopicCard';
import { TOPIC_ICONS } from '../../components/insights/topicIcons';
import { INSIGHTS_TOPICS, totalArticles } from '../../data/insights';
import { insightsOverviewSeo } from '../../seo/insightsSeo';
import { useSeo } from '../../hooks/useSeo';
import { ArrowRight, BookOpen, FileText, Shield } from 'lucide-react';

const HOW_IT_WORKS = [
  { icon: BookOpen, title: 'Ein Leitfaden pro Bereich', text: 'Jeder Themenbereich startet mit einem ausführlichen Hauptartikel: Grundlagen, Auswahlkriterien, Kosten, Einführung und typische Fehler.' },
  { icon: FileText, title: 'Vertiefungen zu Einzelfragen', text: 'Darunter findest du kompakte Artikel zu konkreten Fragestellungen – von Gesetzespflichten bis zur Kalkulation.' },
  { icon: Shield, title: 'Herstellerneutral', text: 'Wir nennen bewusst keine konkreten Systeme oder Anbieter. Es geht um Zusammenhänge und Entscheidungskriterien – nicht um Werbung.' },
];

const InsightsPage = () => {
  useSeo(insightsOverviewSeo());
  const [featured, ...rest] = INSIGHTS_TOPICS;

  return (
    <>
      <Header />
      <main data-testid="insights-page">
        {/* Hero */}
        <section className="sec bg-off">
          <Reveal className="wrap">
            <p className="eyebrow mb-6 md:mb-8"><Star />Insights</p>
            <h1 className="display-1 max-w-[20ch] mb-6">
              Wissen, das deinen Betrieb <span className="serif-it text-purple">digital weiterbringt.</span>
            </h1>
            <p className="text-lg md:text-xl max-w-[62ch] mb-10">
              Ausführliche Leitfäden und praxisnahe Vertiefungen zu allen Bereichen der Gastro-Digitalisierung – verständlich erklärt, herstellerneutral und ohne Verkaufsabsicht.
            </p>
            <div className="grid grid-cols-3 max-w-2xl" data-testid="insights-stats">
              {[
                [INSIGHTS_TOPICS.length, 'Themenbereiche'],
                [totalArticles(), 'Artikel'],
                ['100 %', 'herstellerneutral'],
              ].map(([value, label], i) => (
                <div key={label} className={`border-t-2 border-ink pt-4 pr-4 ${i > 0 ? 'pl-4 border-l border-ink/30' : ''}`}>
                  <span className="block font-display font-extrabold text-3xl md:text-4xl text-purple leading-none mb-1">{value}</span>
                  <span className="text-sm md:text-base">{label}</span>
                </div>
              ))}
            </div>

            <div className="mt-12 flex flex-wrap gap-2.5" data-testid="insights-topic-chips">
              {INSIGHTS_TOPICS.map((t) => {
                const Icon = TOPIC_ICONS[t.icon];
                return (
                  <Link key={t.slug} to={`/insights/${t.slug}`} className="badge-ink badge-ink--white normal-case tracking-normal text-[0.95rem] font-medium hover:bg-lilac transition-colors" data-testid={`insights-chip-${t.slug}`}>
                    {Icon && <Icon className="w-4 h-4 text-purple" />}
                    {t.shortTitle}
                  </Link>
                );
              })}
            </div>
          </Reveal>
        </section>

        {/* How it works */}
        <section className="sec sec--deep">
          <Reveal className="wrap">
            <p className="label-caps text-lilac mb-10">So funktioniert der Bereich</p>
            <div className="grid md:grid-cols-3">
              {HOW_IT_WORKS.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="border-off/30 border-t md:border-t-0 md:border-l first:border-t-0 md:first:border-l-0 py-7 first:pt-0 last:pb-0 md:py-0 md:px-8 md:first:pl-0 md:last:pr-0">
                    <span className="step-num step-num--lilac mb-4">{i + 1}</span>
                    <span className="inline-flex w-11 h-11 items-center justify-center border-2 border-off text-off mb-4">
                      <Icon className="w-5 h-5" />
                    </span>
                    <h3 className="font-display font-bold text-2xl mb-2">{item.title}</h3>
                    <p className="opacity-90 text-[0.98rem] leading-relaxed">{item.text}</p>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </section>

        {/* Topics */}
        <section className="sec bg-off">
          <Reveal className="wrap">
            <div className="mb-12 max-w-3xl">
              <h2 className="display-2 mb-4">Alle Themenbereiche</h2>
              <p className="text-lg md:text-xl">
                Die Bereiche entsprechen unseren Lösungen – so findest du zu jedem Baustein deiner digitalen Landschaft das passende Hintergrundwissen.
              </p>
            </div>
            <div className="mb-10" data-testid="insights-featured">
              <TopicCard topic={featured} featured />
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8" data-testid="insights-topic-grid">
              {rest.map((topic) => (
                <TopicCard key={topic.slug} topic={topic} />
              ))}
            </div>
          </Reveal>
        </section>

        {/* CTA */}
        <section className="sec sec--purple">
          <Reveal className="wrap">
            <h2 className="display-2 max-w-[20ch] mb-4">Lieber direkt <span className="serif-it text-lilac">sprechen</span> statt lesen?</h2>
            <p className="text-lg opacity-90 max-w-[56ch] mb-10">In der kostenlosen Erstberatung schauen wir gemeinsam auf deine digitale Landschaft – auf Augenhöhe und ohne Verkaufsdruck.</p>
            <Link to="/erstberatung" className="btn btn--lilac" data-testid="insights-page-cta">
              Kostenlose Beratung starten
              <ArrowRight className="w-5 h-5" />
            </Link>
          </Reveal>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default InsightsPage;
